import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { createLead } from "@/lib/api";
import { useScrollTo } from "@/lib/useScrollTo";
import { useLang } from "@/lib/i18n";

const SQFT_MID = {
  "Under 1,500 sq ft": 1200,
  "1,500 – 3,000 sq ft": 2250,
  "3,000 – 6,000 sq ft": 4500,
  "Over 6,000 sq ft": 7500,
};
const RATES = { Modern: [320, 520], Neoclassic: [420, 680], Minimalist: [280, 460] };

const computeEstimate = (a) => {
  const sqft = SQFT_MID[a.sqft?.v] || 2250;
  const [lo, hi] = RATES[a.style?.v] || [320, 520];
  const mult = a.property_type?.v === "Villa" ? 1.15 : a.property_type?.v === "Penthouse" ? 1.1 : 1;
  const round = (n) => Math.round(n / 5000) * 5000;
  return [round(sqft * lo * mult), round(sqft * hi * mult)];
};

const slide = {
  initial: { opacity: 0, x: 60 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -60 },
  transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
};

export const Quiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const scrollTo = useScrollTo();
  const { copy } = useLang();
  const q = copy.quiz;

  const fmt = (n) =>
    n >= 1e6
      ? `${q.currency} ${(n / 1e6).toFixed(2).replace(/0$/, "").replace(/\.$/, "")} ${q.million}`
      : `${q.currency} ${Math.round(n / 1000)}${q.thousand}`;

  const select = (key, option) => {
    setAnswers({ ...answers, [key]: option });
    setTimeout(() => setStep((s) => s + 1), 280);
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = q.capture.errName;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = q.capture.errEmail;
    if (!/^[+\d][\d\s-]{6,}$/.test(form.phone.trim())) e.phone = q.capture.errPhone;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const [low, high] = computeEstimate(answers);
    try {
      await createLead({
        ...form,
        property_type: answers.property_type.v,
        area: answers.area.v,
        sqft: answers.sqft.v,
        style: answers.style.v,
        estimate_low: low,
        estimate_high: high,
      });
      setResult({ low, high });
    } catch {
      setErrors({ submit: q.capture.errSubmit });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (err) =>
    `w-full bg-transparent border ${err ? "border-cream/80" : "border-cream/25"} focus:border-cream/60 outline-none px-5 py-4 text-sm font-light tracking-wide placeholder:text-cream/35 transition-colors duration-300`;

  return (
    <section id="quiz" data-testid="quiz-section" className="bg-plum text-cream py-16 md:py-24 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.35em] text-cream/60 mb-6 text-center">{q.overline}</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-center">
            {q.titleA}<br />
            <span className="italic text-cream">{q.titleItalic}</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 md:mt-14 border border-cream/15 bg-plum">
          {!result && (
            <div className="flex items-center justify-between px-6 md:px-10 pt-8">
              <span data-testid="quiz-progress" className="text-[10px] uppercase tracking-[0.35em] text-cream/50">
                {String(Math.min(step + 1, 5)).padStart(2, "0")} — 05
              </span>
              <div className="flex gap-1.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span key={i} className={`h-px w-8 transition-colors duration-500 ${i <= step ? "bg-cream" : "bg-cream/20"}`} />
                ))}
              </div>
            </div>
          )}

          <div className="p-6 md:p-10 min-h-[360px] flex flex-col justify-center">
            <AnimatePresence mode="wait">
              {result ? (
                <motion.div key="result" data-testid="quiz-success" {...slide} className="text-center py-8">
                  <span className="inline-flex items-center justify-center w-14 h-14 border border-cream rounded-full mb-8">
                    <Check size={22} strokeWidth={1.25} className="text-cream" />
                  </span>
                  <p className="text-[11px] uppercase tracking-[0.35em] text-cream/60 mb-4">{q.success.overline}</p>
                  <p data-testid="quiz-estimate-range" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream">
                    {fmt(result.low)} <span className="italic text-cream/70 text-3xl sm:text-4xl lg:text-5xl">{q.success.to}</span> {fmt(result.high)}
                  </p>
                  <p className="mt-6 text-sm font-light text-cream/60 max-w-md mx-auto leading-relaxed">
                    {q.success.text(form.name.split(" ")[0], answers.style?.l, answers.property_type?.l, answers.area?.l)}
                  </p>
                  <button
                    data-testid="quiz-book-call-btn"
                    onClick={() => scrollTo("#booking")}
                    className="mt-10 bg-gold text-cream text-[11px] uppercase tracking-[0.25em] px-10 py-4 hover:bg-cream hover:text-plum transition-colors duration-300"
                  >
                    {q.success.cta}
                  </button>
                </motion.div>
              ) : step < 4 ? (
                <motion.div key={step} {...slide}>
                  <h3 className="font-serif text-2xl md:text-4xl">{q.steps[step].title}</h3>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-cream/50">{q.steps[step].sub}</p>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {q.steps[step].options.map((opt) => (
                      <button
                        key={opt.v}
                        data-testid={`quiz-option-${opt.v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => select(q.steps[step].key, opt)}
                        className={`text-start border px-6 py-5 text-sm tracking-wide font-light transition-colors duration-300 ${
                          answers[q.steps[step].key]?.v === opt.v
                            ? "border-cream/70 bg-cream/5 text-cream"
                            : "border-cream/20 text-cream/85 hover:border-cream/50 hover:text-cream"
                        }`}
                      >
                        {opt.l}
                      </button>
                    ))}
                  </div>
                  {step > 0 && (
                    <button
                      data-testid="quiz-back-btn"
                      onClick={() => setStep(step - 1)}
                      className="mt-8 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-cream/50 hover:text-cream transition-colors duration-300"
                    >
                      <ArrowLeft size={14} strokeWidth={1.25} className="rtl:rotate-180" /> {q.back}
                    </button>
                  )}
                </motion.div>
              ) : (
                <motion.form key="capture" data-testid="quiz-capture-form" {...slide} onSubmit={submit} noValidate>
                  <h3 className="font-serif text-2xl md:text-4xl">{q.capture.title}</h3>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-cream/50">{q.capture.sub}</p>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <input
                        data-testid="quiz-name-input"
                        type="text"
                        placeholder={q.capture.name}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className={inputCls(errors.name)}
                      />
                      {errors.name && <p data-testid="quiz-name-error" className="mt-2 text-xs text-cream/80">{errors.name}</p>}
                    </div>
                    <div>
                      <input
                        data-testid="quiz-email-input"
                        type="email"
                        placeholder={q.capture.email}
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className={inputCls(errors.email)}
                      />
                      {errors.email && <p data-testid="quiz-email-error" className="mt-2 text-xs text-cream/80">{errors.email}</p>}
                    </div>
                    <div>
                      <input
                        data-testid="quiz-phone-input"
                        type="tel"
                        placeholder={q.capture.phone}
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className={inputCls(errors.phone)}
                      />
                      {errors.phone && <p data-testid="quiz-phone-error" className="mt-2 text-xs text-cream/80">{errors.phone}</p>}
                    </div>
                  </div>
                  {errors.submit && <p data-testid="quiz-submit-error" className="mt-4 text-xs text-cream/80">{errors.submit}</p>}
                  <div className="mt-8 flex items-center justify-between">
                    <button
                      type="button"
                      data-testid="quiz-capture-back-btn"
                      onClick={() => setStep(3)}
                      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-cream/50 hover:text-cream transition-colors duration-300"
                    >
                      <ArrowLeft size={14} strokeWidth={1.25} className="rtl:rotate-180" /> {q.back}
                    </button>
                    <button
                      type="submit"
                      data-testid="quiz-submit-btn"
                      disabled={submitting}
                      className="inline-flex items-center gap-3 bg-gold text-plum text-[11px] uppercase tracking-[0.25em] px-8 py-4 hover:bg-cream transition-colors duration-300 disabled:opacity-50"
                    >
                      {submitting ? q.capture.submitting : q.capture.submit} <ArrowRight size={14} strokeWidth={1.25} className="rtl:rotate-180" />
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
